import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { Separator } from '@/components/shadcn/separator'
import { axiosInstance } from '@/api/axios-instance'
import { notify } from '@/lib/notify'
import {
  useGetApiAdministrationAdministratorusers,
  useGetApiCompanies,
  useGetApiAdministrationAdministratorusersAdministratorUserIdPermissions,
  usePostApiAdministrationAdministratorusersAdministratorUserIdPermissionsEntityTypePermissionName,
  useDeleteApiAdministrationAdministratorusersAdministratorUserIdPermissionsEntityTypePermissionName,
  usePostApiAdministrationAdministratorusersAdministratorUserIdPermissionsEntityTypePermissionNameTargetEntityId,
  useDeleteApiAdministrationAdministratorusersAdministratorUserIdPermissionsEntityTypePermissionNameTargetEntityId,
} from '@/api/generated/administrator-user/administrator-user'
import { useGetApiMe } from '@/api/generated/user/user'
import { PermissionEntityType } from '@/api/model/permissionEntityType'
import type { AdministratorUserModel } from '@/api/model'
import { usePermissions } from '@/hooks/usePermissions'
import {
  type Permission,
  type SubcategoryItem,
  ADMINISTRATOR_CATEGORY_ORDER,
} from '@/pages/permissions/permissions.types'
import { CategoryListPanel } from '@/pages/permissions/panel-components/CategoryListPanel'
import { PermissionListPanel } from '@/pages/permissions/panel-components/PermissionListPanel'
import { AdministratorListPanel } from './components/AdministratorListPanel'
import { AdministratorDialog } from './components/AdministratorDialog'

/**
 * Administrator management for Bonisoft staff: the accounts plus the permissions they act with in
 * a customer portal.
 *
 * Deliberately its own page instead of a mode of the company PermissionsPage: that one is company
 * scoped throughout (it reads /api/companies/current/... and its permission endpoints resolve the
 * target user inside the caller's company), while administrators belong to no company.
 *
 * Per-object rows exist for AdministratorCompanies only — a company id is globally unique, while
 * every other TargetEntityId points inside one company and would be inert as soon as the
 * administrator switches. The three category/permission panels are shared with the company page.
 *
 * Gated by two categories, the same the backend enforces: AdministratorPermissions holds View for
 * the page and Edit for the checkboxes, AdministratorUsers holds Create/Edit/Delete for the
 * accounts. Nobody can take Permissions or AdministratorPermissions away from himself.
 */
export default function SuperAdminPermissionsPage() {
  const { t } = useTranslation('administrators')
  const { userHasPermission } = usePermissions()
  const canEditPermissions = userHasPermission(PermissionEntityType.AdministratorPermissions, 'Edit')
  const canCreate = userHasPermission(PermissionEntityType.AdministratorUsers, 'Create')
  const canEditAccounts = userHasPermission(PermissionEntityType.AdministratorUsers, 'Edit')
  const canDeleteAccounts = userHasPermission(PermissionEntityType.AdministratorUsers, 'Delete')

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<PermissionEntityType | null>(null)
  const [administratorPermissions, setAdministratorPermissions] = useState<Record<string, boolean>>({})
  // Keys with a toggle mutation currently in flight → spinner + locked checkbox in the row.
  const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set())
  const [dialog, setDialog] = useState<{ open: boolean; administrator: AdministratorUserModel | null }>({
    open: false,
    administrator: null,
  })

  const { data, isLoading, isError } = useGetApiAdministrationAdministratorusers()
  const administrators: AdministratorUserModel[] = useMemo(() => data?.data ?? [], [data])

  // Own identity — you may not revoke your own permission management (BON-1852).
  const { data: meData } = useGetApiMe()
  const isSelf = !!selectedId && selectedId === meData?.data?.id

  // Permission definitions (no generated hook), same manual call as the company page.
  const { data: definitionsData } = useQuery({
    queryKey: ['permissionDefinitions'],
    queryFn: () => axiosInstance<Permission[]>({ url: '/api/permissions/definitions', method: 'GET' }).then(r => r.data),
  })

  // The companies feed the per-object rows of the AdministratorCompanies category — the one
  // category where a per entity permission is meaningful for an administrator, because a company
  // id is globally unique and does not depend on the portal he currently acts in.
  const { data: companiesData } = useGetApiCompanies()
  const companyItems: SubcategoryItem[] = useMemo(
    () => (companiesData?.data ?? []).map(company => ({ id: company.id!, name: company.name ?? String(company.id) })),
    [companiesData]
  )

  // Per-object definitions are kept only for AdministratorCompanies. Everywhere else a
  // TargetEntityId points at something inside one company, which is meaningless for an
  // administrator who switches between them.
  //
  // Deduplicated by (entityType, permissionName, isPerEntity): the seeder only skips *new*
  // duplicates, so an existing double row would render the same checkbox twice.
  const categorizedPermissions = useMemo(() => {
    const definitions: Permission[] = Array.isArray(definitionsData) ? definitionsData : []
    const seen = new Set<string>()
    return definitions
      .filter(perm => !perm.isPerEntity || perm.entityType === PermissionEntityType.AdministratorCompanies)
      .reduce<Partial<Record<PermissionEntityType, { global: Permission[]; perEntity: Permission[] }>>>((acc, perm) => {
        const dedupeKey = `${perm.entityType}.${perm.permissionName}.${perm.isPerEntity ? 'entity' : 'global'}`
        if (seen.has(dedupeKey)) return acc
        seen.add(dedupeKey)
        if (!acc[perm.entityType]) acc[perm.entityType] = { global: [], perEntity: [] }
        if (perm.isPerEntity) acc[perm.entityType]!.perEntity.push(perm)
        else acc[perm.entityType]!.global.push(perm)
        return acc
      }, {})
  }, [definitionsData])

  const categories = useMemo(
    () => ADMINISTRATOR_CATEGORY_ORDER.filter(entityType => categorizedPermissions[entityType]),
    [categorizedPermissions]
  )

  const {
    data: permissionsData,
    refetch: refetchPermissions,
    isLoading: permissionsLoading,
    isError: permissionsError,
  } = useGetApiAdministrationAdministratorusersAdministratorUserIdPermissions(
    selectedId ?? '', {},
    { query: { enabled: !!selectedId } }
  )

  useEffect(() => {
    const assignments = permissionsData?.data ?? []
    const map: Record<string, boolean> = {}
    assignments.forEach((assignment) => {
      const globalKey = `${assignment.entityType}.${assignment.permissionName}`
      map[assignment.targetEntityId != null ? `${globalKey}.${assignment.targetEntityId}` : globalKey] = true
    })
    setAdministratorPermissions(map)
  }, [permissionsData])

  // No administrator is preselected on purpose: the page would otherwise open on somebody's
  // rights without anyone having picked him, which invites a toggle on the wrong account. The
  // category keeps its preselection — it carries no account.
  useEffect(() => {
    if (!selectedCategory && categories.length > 0) setSelectedCategory(categories[0])
  }, [categories, selectedCategory])

  const addPermission = usePostApiAdministrationAdministratorusersAdministratorUserIdPermissionsEntityTypePermissionName()
  const removePermission = useDeleteApiAdministrationAdministratorusersAdministratorUserIdPermissionsEntityTypePermissionName()
  const addEntityPermission = usePostApiAdministrationAdministratorusersAdministratorUserIdPermissionsEntityTypePermissionNameTargetEntityId()
  const removeEntityPermission = useDeleteApiAdministrationAdministratorusersAdministratorUserIdPermissionsEntityTypePermissionNameTargetEntityId()

  // Toggle optimistically, roll back on error. targetEntityId is only ever a company id — the
  // per-object rows exist for AdministratorCompanies alone.
  async function handleToggle(perm: Permission, targetEntityId?: number) {
    if (!selectedId) return
    if (isSelf &&
      (perm.entityType === PermissionEntityType.Permissions ||
        perm.entityType === PermissionEntityType.AdministratorPermissions)) return

    const globalKey = `${perm.entityType}.${perm.permissionName}`
    const key = targetEntityId != null ? `${globalKey}.${targetEntityId}` : globalKey
    const current = administratorPermissions[key] ?? false
    // Ignore double-clicks while this row's mutation is still running.
    if (pendingKeys.has(key)) return

    setAdministratorPermissions(prev => ({ ...prev, [key]: !current }))
    setPendingKeys(prev => new Set(prev).add(key))

    try {
      const variables = {
        administratorUserId: selectedId,
        entityType: perm.entityType,
        permissionName: perm.permissionName,
      }
      if (targetEntityId != null) {
        const entityVariables = { ...variables, targetEntityId }
        if (current) {
          await removeEntityPermission.mutateAsync(entityVariables)
        } else {
          await addEntityPermission.mutateAsync(entityVariables)
        }
      } else if (current) {
        await removePermission.mutateAsync(variables)
      } else {
        await addPermission.mutateAsync(variables)
      }
      refetchPermissions()
    } catch (error) {
      setAdministratorPermissions(prev => ({ ...prev, [key]: current }))
      notify.apiError(error, t('toasts.permissionError'))
    } finally {
      setPendingKeys(prev => {
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }
  }

  return (
    <PageLayout>
      <PageHeader title={t('header.title')} description={t('header.description')} />

      <div className="flex w-full gap-0 border rounded-lg overflow-hidden flex-1 min-h-[500px]">
        <AdministratorListPanel
          administrators={administrators}
          isLoading={isLoading}
          isError={isError}
          selectedId={selectedId}
          onSelect={(administrator) => setSelectedId(administrator.id)}
          onAdd={() => setDialog({ open: true, administrator: null })}
          onEdit={(administrator) => setDialog({ open: true, administrator })}
          canCreate={canCreate}
          canEdit={canEditAccounts}
        />

        <Separator orientation="vertical" />

        <CategoryListPanel
          categories={categories}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
        />

        <Separator orientation="vertical" />

        <PermissionListPanel
          selectedUserId={selectedId}
          selectedCategory={selectedCategory}
          permissions={selectedCategory ? categorizedPermissions[selectedCategory] : undefined}
          items={selectedCategory === PermissionEntityType.AdministratorCompanies ? companyItems : []}
          userPermissions={administratorPermissions}
          pendingKeys={pendingKeys}
          isLoading={permissionsLoading}
          isError={permissionsError}
          onToggle={handleToggle}
          isSelf={isSelf}
          canEdit={canEditPermissions}
          globalOnly={selectedCategory !== PermissionEntityType.AdministratorCompanies}
        />
      </div>

      <AdministratorDialog
        isOpen={dialog.open}
        onOpenChange={(open) => setDialog((prev) => ({ ...prev, open }))}
        administrator={dialog.administrator}
        canDelete={canDeleteAccounts}
        onDeleted={(administratorId) => { if (selectedId === administratorId) setSelectedId(null) }}
      />
    </PageLayout>
  )
}
